import classNames from './ClassEditor.module.scss';
import classes from 'classnames';
import SVG from 'react-inlinesvg';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import commonUtils from '@/utils/common';
import { ClassConfig, TeacherGroup } from '@/types/user';
import { useAppSelector } from '@/store';
import { teacherDashboardSelector } from '@/store/reducers/teacher-dashboard';
import { registerService } from '@/services/register';

import Detail from '../UserInfo/Detail/Detail';
import DetailSeperator from '../DetailSeperator/DetailSeperator';
import TheButton from '../TheButton/TheButton';
import AppInput from '../AppInput/AppInput';
import SectionSeperator from '../SectionSeperator/SectionSeperator';
import { useNavigate } from 'react-router-dom';
import { authSelector } from '@/store/reducers/auth';

import upArrow from '@/assets/svg/up-arrow.svg';
import downArrow from '@/assets/svg/down-arrow.svg';
import pencilBlack from '@/assets/svg/pencilBlack.svg';
import copyIcon from '@/assets/svg/copy.svg';
import threeStudentsIcon from '@/assets/svg/three_students2.svg';
import deleteIcon from '@/assets/svg/delete.svg';
import AppLoaderCircle from '../AppLoaderCircle';

import DealLimitReachedMessage from '@/components/DealLimitReachedMessage/DealLimitReachedMessage';
import FullClassModal from '@/components/FullClassModal/FullClassModal';
import LoaderModal from '@/components/LoaderModal/LoaderModal';
import DeleteClass from './DeleteClass/DeleteClass';
import config from '@/config';
import MultiSelectDropDown, {
  MultiSelectOption,
} from '@/components/school-management/MultiSelectDropDown/MultiSelectDropDown';
import { useCoTeacherSelect } from '@/hooks/useCoTeacherSelect';
import { progressService } from '@/services/progress';
import { parseTeacherOverviewResponse } from '@/components/ClassOverview/teacherOverviewClass';
import ToggleSwitch from '@/components/ToggleSwitch/ToggleSwitch';
import LevelSettingsConfirmModal from '@/components/ClassEditor/LevelSettingsConfirmModal/LevelSettingsConfirmModal';
import {
  LEVEL_CONTROL_DEFAULTS,
  applyPlacementTestToggle,
  getAdvancementConfirmLabel,
  getAdvancementWarning,
  getDefaultStartingLevelConfirmLabel,
  getDefaultStartingLevelWarning,
  getLevelControlOptions,
  getPlacementTestConfirmLabel,
  getPlacementTestWarning,
  isLevelControlValid,
  normalizeStartingLevel,
} from '@/school-management/utils/levelControl';

const CLASS_NAME_PATTERN = /^[A-Za-z0-9\s'"()–\-:/&*.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/;

interface ClassEditorProps {
  className?: string;
  loading?: boolean;
  classConfig: ClassConfig | null;
  currentClassroom: TeacherGroup | null;
  showCopyModal: boolean;
  setShowCopyModal: (val: boolean) => void;
  onShowCopyModal: () => void;
  onAbortAddClass?: () => void;
  createOrSaveClass: (
    newConfig: ClassConfig,
    options?: { skipAddStudentsModal?: boolean }
  ) => Promise<any>;
  setIsClassEdit?: React.Dispatch<React.SetStateAction<boolean>>;
  onShowUploadCsvModal?: (isNewGroup?: boolean) => void;
  isClassDeleting: boolean;
  showClassDeleteError: boolean;
  setShowClassDeleteError: React.Dispatch<React.SetStateAction<boolean>>;
  onClassDelete: (reason?: string) => void;
  onClassCreated: () => void;
  allowAddingStudents?: boolean;
}

const ClassEditor = ({
  className,
  classConfig,
  currentClassroom,
  loading,
  createOrSaveClass,
  onShowCopyModal,
  onAbortAddClass,
  setIsClassEdit,
  onShowUploadCsvModal,
  isClassDeleting,
  showClassDeleteError,
  setShowClassDeleteError,
  onClassDelete,
  onClassCreated,
  allowAddingStudents = true,
}: ClassEditorProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedClassId } = useAppSelector(teacherDashboardSelector);
  const { user } = useAppSelector(authSelector);
  const [classNameValue, setClassNameValue] = useState('');
  const [gradeValue, setGradeValue] = useState('');
  const [invitationLink, setInvitationLink] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const isMGWebsite = commonUtils.isMGWebsite();
  const isClassCodeOrg = commonUtils.isClassCode(user);
  const isCreateClassFlow = !classConfig?.id;
  const organizationId = user?.metadata?.organizationDetails?.organizationId || '';
  const currentTeacherId = user?.metadata?.id;
  const [coTeachersValue, setCoTeachersValue] = useState<MultiSelectOption[]>([]);

  const preselectedCoTeacherIds = useMemo(
    () => coTeachersValue.map(option => String(option.value)),
    [coTeachersValue]
  );

  const {
    coTeacherOptions,
    handleLoadMore: handleLoadMoreCoTeachers,
    hasMoreTeachers: hasMoreCoTeachers,
    isLoadingMore: isLoadingMoreCoTeachers,
    isLoadingFirstPage: isLoadingCoTeachers,
  } = useCoTeacherSelect({
    organizationId,
    currentTeacherId,
    preselectedTeacherIds: preselectedCoTeacherIds,
    enabled: Boolean(organizationId),
  });

  const grades = Array.from({ length: 14 }, (_, i) => {
    return {
      name: t(`grade_${i + 1}`),
      value: (i + 1).toString(),
    };
  });

  const [placementTestRequired, setPlacementTestRequired] = useState<boolean>(
    LEVEL_CONTROL_DEFAULTS.placementTestRequired
  );
  const [defaultStartingLevel, setDefaultStartingLevel] = useState<string | null>(
    LEVEL_CONTROL_DEFAULTS.defaultStartingLevel
  );
  const [allowIndividualAdvancement, setAllowIndividualAdvancement] = useState<boolean>(
    LEVEL_CONTROL_DEFAULTS.allowIndividualAdvancement
  );
  const [showLevelDropdown, setShowLevelDropdown] = useState(false);
  const [defaultStartingLevelError, setDefaultStartingLevelError] = useState('');

  // Distinct from `grades` above: the class's school grade (1-14) and a student's system level
  // (LEVEL_1..LEVEL_5) are different catalogs. The backend's UserLevel converter rejects a bare
  // grade id, so the starting-level dropdown must use these options, not `grades`.
  const levelOptions = getLevelControlOptions(t);
  const selectedLevelOption = levelOptions.find(option => option.value === defaultStartingLevel);
  const [pendingLevelChange, setPendingLevelChange] = useState<{
    message: string;
    confirmLabel: string;
    apply: () => void;
  } | null>(null);

  const [displayedLevelChange, setDisplayedLevelChange] = useState<{
    message: string;
    confirmLabel: string;
  } | null>(null);

  const confirmLevelChange = (message: string, confirmLabel: string, apply: () => void) => {
    setPendingLevelChange({ message, confirmLabel, apply });
    setDisplayedLevelChange({ message, confirmLabel });
  };

  const [classNameValueError, setClassNameValueError] = useState('');
  const [gradeValueError, setGradeValueError] = useState('');

  const [showFullClassModal, setShowFullClassModal] = useState<boolean>(false);
  const [showDealLimitReachedModal, setShowDealLimitReachedModal] = useState<boolean>(false);
  const [isOrganizationDetailsLoading, setIsOrganizationDetailsLoading] = useState<boolean>(false);
  const isComponentActive = useRef<boolean>(false);
  const coTeacherPrefillFromConfigRef = useRef<string>('');

  useEffect(() => {
    isComponentActive.current = true;

    return () => {
      isComponentActive.current = false;
    };
  }, []);

  useEffect(() => {
    onReset();
  }, [classConfig]);

  useEffect(() => {
    const coTeacherIds = classConfig?.coTeacherIds ?? [];
    const configKey = [...coTeacherIds].sort().join(',');

    if (!configKey) {
      coTeacherPrefillFromConfigRef.current = '';
      return;
    }

    if (coTeacherPrefillFromConfigRef.current === configKey) {
      return;
    }

    const nextCoTeachers = coTeacherIds
      .map(teacherId => coTeacherOptions.find(option => option.value === teacherId))
      .filter((option): option is MultiSelectOption => Boolean(option));

    if (nextCoTeachers.length === 0) {
      return;
    }

    setCoTeachersValue(prev => {
      const prevKey = prev
        .map(option => String(option.value))
        .sort()
        .join(',');

      if (prevKey !== '' && prevKey !== configKey) {
        coTeacherPrefillFromConfigRef.current = configKey;
        return prev;
      }

      coTeacherPrefillFromConfigRef.current = configKey;
      return nextCoTeachers;
    });
  }, [classConfig?.coTeacherIds, coTeacherOptions]);

  useEffect(() => {
    if (!classConfig?.id || !user || classConfig.coTeacherIds?.length) {
      return;
    }

    let isActive = true;

    const loadCoTeacher = async () => {
      try {
        const raw = await progressService.fetchOverviewClassProgress(user, classConfig.id!);
        const cp = parseTeacherOverviewResponse(raw);
        const coTeacher = cp?.groupDetails?.additionalTeacherName;
        if (!coTeacher?.id) {
          return;
        }

        if (!isActive) {
          return;
        }

        setCoTeachersValue([
          {
            label: `${coTeacher.firstName} ${coTeacher.lastName}`.trim(),
            value: coTeacher.id,
          },
        ]);
      } catch (error) {
        console.error('Error loading co-teacher for class edit:', error);
      }
    };

    void loadCoTeacher();

    return () => {
      isActive = false;
    };
  }, [classConfig?.id, classConfig?.coTeacherIds, user]);

  useEffect(() => {
    window.addEventListener('click', () => {
      if (!showDropdown) {
        return;
      }

      onCloseDropdown();
    });

    return () => {
      window.removeEventListener('click', () => {});
    };
  }, [showDropdown]);

  useEffect(() => {
    const hideLevelDropdown = () => {
      if (!showLevelDropdown) {
        return;
      }

      setShowLevelDropdown(false);
    };

    window.addEventListener('click', hideLevelDropdown);

    return () => {
      window.removeEventListener('click', hideLevelDropdown);
    };
  }, [showLevelDropdown]);

  const onReset = () => {
    setClassNameValue(classConfig?.name ?? '');
    setGradeValue(classConfig?.grade ?? '1');
    setInvitationLink(classConfig?.invitationLinkForStudents ?? '');
    setPlacementTestRequired(
      classConfig?.placementTestRequired ?? LEVEL_CONTROL_DEFAULTS.placementTestRequired
    );
    setDefaultStartingLevel(
      normalizeStartingLevel(classConfig?.defaultStartingLevel) ??
        LEVEL_CONTROL_DEFAULTS.defaultStartingLevel
    );
    setAllowIndividualAdvancement(
      classConfig?.allowIndividualAdvancement ?? LEVEL_CONTROL_DEFAULTS.allowIndividualAdvancement
    );
    setDefaultStartingLevelError('');
    if (!classConfig?.id) {
      setCoTeachersValue([]);
      coTeacherPrefillFromConfigRef.current = '';
    }

    setClassNameValueError('');
    setGradeValueError('');
    setPendingLevelChange(null);
  };

  const onToggleDropdown = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setShowDropdown(prevState => !prevState);
  };

  const onCloseDropdown = () => {
    setShowDropdown(false);
  };

  const onSelectedOption = (e: React.MouseEvent<HTMLElement>, option: string) => {
    e.stopPropagation();
    onCloseDropdown();
    setGradeValue(option);
    setGradeValueError('');
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  };

  const onCopy = () => {
    onShowCopyModal();
    navigator.clipboard.writeText(invitationLink);
  };

  const onCancel = () => {
    setPendingLevelChange(null);

    if (classConfig?.id) {
      setIsClassEdit?.(false);
      return;
    }

    if (user?.metadata.role === 'teacher') {
      onAbortAddClass?.();
    } else {
      scrollToTop();
      navigate('/dashboard/my-students');
    }
  };

  const getClassNameError = (value: string): string => {
    const trimmed = value.trim();

    if (!trimmed) {
      return t('editClassInvalidClassName') ?? ' ';
    }

    if (value.length > config.classNameMaxLength) {
      return t('longClassName') ?? ' ';
    }

    if (!CLASS_NAME_PATTERN.test(trimmed)) {
      return t('editClassInvalidClassNameFormat') ?? ' ';
    }

    return '';
  };

  const onClassNameBlur = () => {
    setClassNameValueError(getClassNameError(classNameValue));
  };

  const buildNewClassPayload = (): ClassConfig | null => {
    const classNameError = getClassNameError(classNameValue);
    const isGradeValid = gradeValue.trim();
    const levelControlValid = isLevelControlValid({
      placementTestRequired,
      defaultStartingLevel,
      allowIndividualAdvancement,
    });

    if (classNameError) {
      setClassNameValueError(classNameError);
    }

    if (!isGradeValid) {
      setGradeValueError(t('editClassInvalidGrade') ?? ' ');
    }

    if (!levelControlValid) {
      setDefaultStartingLevelError(
        t('admin.schoolDetailsPage.classes.levelControl.defaultStartingLevelRequired') ?? ' '
      );
    }

    if (classNameError || !isGradeValid || !levelControlValid) {
      return null;
    }

    return {
      name: classNameValue.trim(),
      grade: gradeValue,
      levelTest: placementTestRequired,
      coTeacherIds: coTeachersValue.map(option => String(option.value)),
      placementTestRequired,
      defaultStartingLevel: placementTestRequired ? null : defaultStartingLevel,
      allowIndividualAdvancement,
    };
  };

  const coTeacherField = (
    <div className={classNames.createClassFieldGroup}>
      <label className={classNames.createClassFieldLabel}>
        {t('admin.schoolDetailsPage.classes.viewClassPage.coTeacher')}
      </label>
      <MultiSelectDropDown
        placeholder={t('admin.schoolDetailsPage.classes.teachersPlaceholder') || ''}
        searchPlaceholder={t('admin.schoolDetailsPage.classes.teachersSearchPlaceholder') || ''}
        options={coTeacherOptions}
        value={coTeachersValue}
        onChange={setCoTeachersValue}
        maxSelections={1}
        maxHeight='500px'
        onLoadMore={handleLoadMoreCoTeachers}
        hasMore={hasMoreCoTeachers}
        isLoadingMore={isLoadingMoreCoTeachers}
        enableAutocomplete={true}
        organizationId={organizationId}
        autocompleteType='teachers'
        autocompleteStartOptions={coTeacherOptions}
        excludeValues={currentTeacherId ? [currentTeacherId] : []}
        isLoadingOptions={isLoadingCoTeachers}
        headerClassName={isCreateClassFlow ? classNames.createClassCoTeacherHeader : undefined}
      />
    </div>
  );

  // Each level control change is confirmed individually as it happens (see levelControlSection
  // below), so submitting the rest of the form doesn't need its own diff/confirm step.
  const submitCreateOrSave = (skipAddStudentsModal?: boolean) => {
    const payload = buildNewClassPayload();
    if (!payload) {
      return;
    }

    createOrSaveClass(
      payload,
      skipAddStudentsModal ? { skipAddStudentsModal: true } : undefined
    ).then((response: any) => {
      const classCode = response?.classCode;

      if (!classConfig?.id && classCode) {
        onClassCreated?.();
      }
      if (setIsClassEdit && classConfig?.id) {
        setIsClassEdit(false);
      }
    });
  };

  const onNext = () => {
    submitCreateOrSave(false);
  };

  const onSaveClassAddStudentsLater = () => {
    submitCreateOrSave(true);
  };

  const openAddStudent = async () => {
    if (isOrganizationDetailsLoading) {
      return;
    }
    setIsOrganizationDetailsLoading(true);

    let isAllowedToRegister: boolean = false;

    try {
      isAllowedToRegister = !(await registerService.isRegistrationLimitReached(user))?.data.data;
    } catch (error: any) {
      const response = error?.response;
      const status = response?.status;
      const message = response?.data?.message;

      if (
        status === 403 &&
        message === 'User registration limit reached. No new users can be registered at this time.'
      ) {
        setShowDealLimitReachedModal(true);
      } else {
        commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
      }
      setIsOrganizationDetailsLoading(false);
      return;
    }

    if (!isComponentActive.current) {
      return;
    }

    setIsOrganizationDetailsLoading(false);

    if (!isAllowedToRegister) {
      setShowDealLimitReachedModal(true);
    } else if ((currentClassroom?.totalStudents || 0) < 50) {
      onShowUploadCsvModal?.(false);
    } else {
      setShowFullClassModal(true);
    }
  };

  const onCloseFullClassModal = useCallback(() => {
    setShowFullClassModal(false);
  }, []);

  const onCloseDealLimitReachedModal = useCallback(() => {
    setShowDealLimitReachedModal(false);
  }, []);

  const studentsAmount = 0;

  const gradeOptions = grades.map(grade => {
    return (
      <div
        key={grade.name}
        className={classNames.grade}
        onClick={event => onSelectedOption(event, grade.value)}
      >
        {grade.name}
      </div>
    );
  });

  const gradesDropdown = showDropdown && (
    <div className={classNames.gradesDropdown}>{gradeOptions}</div>
  );

  const gradesSelect = (
    <div
      className={classes(classNames.gradesSelectContainer, {
        [classNames.gradesSelectContainerError]: gradeValueError,
        [classNames.gradesSelectContainerCreate]: isCreateClassFlow,
      })}
    >
      <span className={classNames.label}>
        {isCreateClassFlow ? `${t('admin.schoolDetailsPage.classes.gradeLabel')} *` : t('grade')}
      </span>
      <div className={classes(classNames.gradesSelect)} onClick={onToggleDropdown}>
        <span className={!gradeValue ? 'opacity-40' : ''}>
          {gradeValue ? t(`grade_${gradeValue}`) : t('choose')}
        </span>
        <SVG src={showDropdown ? upArrow : downArrow} />
      </div>
      {gradesDropdown}
      {gradeValueError && <span className={classNames.gradesError}>{gradeValueError}</span>}
    </div>
  );

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const onOpenDeleteModal = useCallback(() => {
    setShowClassDeleteError(false);
    setShowDeleteModal(true);
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const onDelete = useCallback(() => {
    onClassDelete();
  }, []);

  const isCreateSubmitDisabled =
    !classNameValue.trim() ||
    !gradeValue.trim() ||
    classNameValue.length > config.classNameMaxLength ||
    !isLevelControlValid({
      placementTestRequired,
      defaultStartingLevel,
      allowIndividualAdvancement,
    });

  const levelControlSection = (
    <div className='flex flex-col gap-y-5 w-full'>
      <div className='flex items-center justify-between gap-4'>
        <div className='flex flex-col gap-y-1'>
          <span className={classNames.label}>
            {t('admin.schoolDetailsPage.classes.levelControl.placementTestRequiredLabel')}
          </span>
          <span className={classNames.fieldDescription}>
            {t('admin.schoolDetailsPage.classes.levelControl.placementTestRequiredTooltip')}
          </span>
        </div>
        <ToggleSwitch
          checked={placementTestRequired}
          disabled={Boolean(pendingLevelChange)}
          onChange={value => {
            confirmLevelChange(
              getPlacementTestWarning(value, isCreateClassFlow, t, 'admin.'),
              getPlacementTestConfirmLabel(value, t, 'admin.'),
              () => {
                const next = applyPlacementTestToggle(
                  { placementTestRequired, defaultStartingLevel, allowIndividualAdvancement },
                  value
                );
                setPlacementTestRequired(next.placementTestRequired);
                setDefaultStartingLevel(next.defaultStartingLevel);
                setDefaultStartingLevelError('');
              }
            );
          }}
        />
      </div>

      {!placementTestRequired && (
        <div
          className={classes(classNames.gradesSelectContainerCreate, {
            [classNames.gradesSelectContainerError]: defaultStartingLevelError,
          })}
          style={{ maxWidth: 320 }}
        >
          <span className={classNames.label}>
            {`${t('admin.schoolDetailsPage.classes.levelControl.defaultStartingLevelLabel')} *`}
          </span>
          <div
            className={classNames.gradesSelect}
            onClick={e => {
              e.stopPropagation();
              setShowLevelDropdown(prev => !prev);
            }}
          >
            <span className={!defaultStartingLevel ? 'opacity-40' : ''}>
              {selectedLevelOption ? selectedLevelOption.label : t('choose')}
            </span>
            <SVG src={showLevelDropdown ? upArrow : downArrow} />
          </div>
          {showLevelDropdown && (
            <div className={classNames.gradesDropdown}>
              {levelOptions.map(option => (
                <div
                  key={option.value}
                  className={classNames.grade}
                  onClick={e => {
                    e.stopPropagation();
                    setShowLevelDropdown(false);
                    confirmLevelChange(
                      getDefaultStartingLevelWarning(isCreateClassFlow, t, 'admin.'),
                      getDefaultStartingLevelConfirmLabel(isCreateClassFlow, t, 'admin.'),
                      () => {
                        setDefaultStartingLevel(option.value);
                        setDefaultStartingLevelError('');
                      }
                    );
                  }}
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
          {defaultStartingLevelError && (
            <span className={classNames.gradesError}>{defaultStartingLevelError}</span>
          )}
        </div>
      )}

      <div className='flex items-center justify-between gap-4'>
        <div className='flex flex-col gap-y-1'>
          <span className={classNames.label}>
            {t('admin.schoolDetailsPage.classes.levelControl.allowIndividualAdvancementLabel')}
          </span>
          <span className={classNames.fieldDescription}>
            {t('admin.schoolDetailsPage.classes.levelControl.allowIndividualAdvancementTooltip')}
          </span>
        </div>
        <ToggleSwitch
          checked={allowIndividualAdvancement}
          disabled={Boolean(pendingLevelChange)}
          onChange={value => {
            confirmLevelChange(
              getAdvancementWarning(value, isCreateClassFlow, t, 'admin.'),
              getAdvancementConfirmLabel(value, t, 'admin.'),
              () => {
                setAllowIndividualAdvancement(value);
              }
            );
          }}
        />
      </div>
    </div>
  );

  return (
    <div className={classes(classNames.classEditor, className)}>
      <FullClassModal show={showFullClassModal} onClose={onCloseFullClassModal} />
      <DeleteClass
        show={showDeleteModal}
        isClassDeleting={isClassDeleting}
        showClassDeleteError={showClassDeleteError}
        onClose={onCloseDeleteModal}
        onDelete={onDelete}
        studentsAmount={(currentClassroom?.totalStudents && currentClassroom.totalStudents) || 0}
        setShowClassDeleteError={setShowClassDeleteError}
      />
      <DealLimitReachedMessage
        show={showDealLimitReachedModal}
        onClose={onCloseDealLimitReachedModal}
      />
      <LevelSettingsConfirmModal
        show={Boolean(pendingLevelChange)}
        messages={displayedLevelChange ? [displayedLevelChange.message] : []}
        confirmLabel={displayedLevelChange?.confirmLabel}
        onConfirm={() => {
          pendingLevelChange?.apply();
          setPendingLevelChange(null);
        }}
        onCancel={() => setPendingLevelChange(null)}
      />
      {isOrganizationDetailsLoading && <LoaderModal show={true} />}
      <div className={classNames.classEditorInner}>
        {isCreateClassFlow ? (
          <div className={classNames.createClassFormCard}>
            <div className={classNames.createClassFormFields}>
              <div className={classNames.createClassFieldGroup}>
                <AppInput
                  className={classes(classNames.createClassTextField, {
                    [classNames.classNameFieldError]: classNameValueError,
                  })}
                  inputWrapperClassName={classNames.createClassInputWrap}
                  error={classNameValueError}
                  id='class_name'
                  name='className'
                  label={`${t('admin.schoolDetailsPage.classes.className')} *`}
                  placeholder={
                    String(t('admin.schoolDetailsPage.newClassForm.classNamePlaceholder') || '') ||
                    String(t('writeHere') || '')
                  }
                  value={classNameValue}
                  maxLength={config.classNameMaxLength}
                  emitChange={({ target }) => {
                    setClassNameValue(target.value);
                    setClassNameValueError('');
                  }}
                  emitBlur={onClassNameBlur}
                />
              </div>
              <div className={classNames.createClassFieldGroup}>{gradesSelect}</div>
              {coTeacherField}
              <div
                className={classes(
                  classNames.createClassFieldGroup,
                  classNames.createClassLevelControlGroup
                )}
              >
                {levelControlSection}
              </div>
            </div>
            <div className={classNames.createClassActions}>
              <button
                type='button'
                className={classNames.createClassCancelBtn}
                onClick={onCancel}
                disabled={loading}
              >
                {t('cancel')}
              </button>
              <div className={classNames.createClassRightBtns}>
                {loading ? (
                  <div className={classNames.createClassActionsLoading}>
                    <AppLoaderCircle />
                  </div>
                ) : (
                  <>
                    {!isClassCodeOrg && (
                      <button
                        type='button'
                        className={classNames.createClassSaveLaterBtn}
                        disabled={isCreateSubmitDisabled}
                        onClick={onSaveClassAddStudentsLater}
                      >
                        {t('admin.schoolDetailsPage.classes.saveClassAndAddLater')}
                      </button>
                    )}
                    {!isClassCodeOrg && allowAddingStudents && (
                      <button
                        type='button'
                        className={classNames.createClassContinueBtn}
                        disabled={isCreateSubmitDisabled}
                        onClick={onNext}
                      >
                        {t('admin.schoolDetailsPage.classes.continueToUploadingStudents')}
                      </button>
                    )}
                    {isClassCodeOrg && (
                      <button
                        type='button'
                        className={classNames.createClassContinueBtn}
                        disabled={isCreateSubmitDisabled}
                        onClick={onNext}
                      >
                        {t('admin.schoolDetailsPage.classes.create')}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className={classNames.header}>
              <div className={classNames.texts}>
                <h2>{classNameValue || t('className')}</h2>
                <div className={classNames.details}>
                  {gradeValue && (
                    <Detail
                      className={classNames.detail}
                      topic={t('grade')}
                      value={t(`grade_${gradeValue}`)}
                    />
                  )}
                  {(!!currentClassroom?.totalStudents || currentClassroom?.totalStudents === 0) &&
                    classConfig?.id && (
                      <>
                        <DetailSeperator />
                        <Detail
                          className={classNames.detail}
                          topic={t('students')}
                          value={`${currentClassroom.totalStudents}/50`}
                        />
                      </>
                    )}

                  {studentsAmount > 0 && (
                    <>
                      <DetailSeperator />
                      <Detail
                        className={classNames.detail}
                        topic={t('students')}
                        value={studentsAmount}
                      />
                    </>
                  )}
                </div>
              </div>
              {classConfig?.id && !isMGWebsite && (
                <button
                  onClick={() => {
                    scrollToTop();
                    if (setIsClassEdit) {
                      setIsClassEdit(false);
                    }
                  }}
                  className={classNames.editIcon}
                  aria-label={t('scrollToTop').toString()}
                >
                  <SVG src={pencilBlack} />
                </button>
              )}
            </div>
            <SectionSeperator className='mt-6 mb-8' />
            <div className={classNames.form}>
              <div className={classes(classNames.fieldsGroup, 'flex flex-col gap-y-6')}>
                <div className={classNames.nameAndGradeFields}>
                  <AppInput
                    className={classes(classNames.field, {
                      [classNames.classNameFieldError]: classNameValueError,
                    })}
                    error={classNameValueError}
                    id='class_name'
                    name='className'
                    label={t('className') ?? ''}
                    placeholder={t('writeHere') ?? ''}
                    value={classNameValue}
                    maxLength={config.classNameMaxLength}
                    emitChange={({ target }) => {
                      setClassNameValue(target.value);
                      setClassNameValueError('');
                    }}
                    emitBlur={onClassNameBlur}
                  />
                  {gradesSelect}
                </div>
                {coTeacherField}
                {levelControlSection}
                {selectedClassId && invitationLink && (
                  <div className={classNames.invitationLinkField}>
                    <span className={classNames.label}>{t('invitationLink')}</span>
                    <button onClick={onCopy} aria-label={t('invitationLink').toString()}>
                      <span>{invitationLink}</span>
                      <SVG src={copyIcon} />
                    </button>
                  </div>
                )}
              </div>
              {classConfig?.id && classConfig?.id !== undefined && !currentClassroom?.classCode && (
                <div className={classNames.tools}>
                  <button
                    onClick={openAddStudent}
                    aria-label={t('editClassInviteNewStudent').toString()}
                  >
                    <span>
                      <SVG src={threeStudentsIcon} />
                    </span>
                    <span>{t('editClassInviteNewStudent')}</span>
                  </button>
                </div>
              )}
              <div className={classNames.bottom}>
                <div className={classNames.buttons}>
                  <TheButton
                    className={classNames.cancelBtn}
                    text={t('cancel')}
                    plain
                    emitOnClick={onCancel}
                  />
                  {loading ? (
                    <div className={classNames.loading}>
                      <AppLoaderCircle />
                    </div>
                  ) : (
                    <TheButton
                      className={classNames.nextBtn}
                      text={t('save')}
                      shouldRecolor={false}
                      emitOnClick={onNext}
                    />
                  )}
                </div>
                {classConfig?.id && (
                  <button
                    className={classNames.deleteBtn}
                    onClick={onOpenDeleteModal}
                    aria-label={t('deleteClass').toString()}
                  >
                    <img src={deleteIcon} />
                    <span>{t('deleteClass')}</span>
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ClassEditor;
